import type { CellKind, ReportTable } from './reports.js';
import type { ReportSource } from './types.js';

/**
 * Raporun Excel dosyası (ADR 0040 #189; mimari belge "Teknoloji": exceljs). İstemcide üretilir:
 * gemi uygulaması internetsiz çalışır, ofiste de sunucuya iş düşmez. exceljs ilk kullanımda ayrı
 * parça olarak yüklenir. Gün gerçek tarih, tutar ve miktar sayı hücresidir (süzülür, toplanır).
 */

interface Sheet {
  addRow(values: unknown[]): { font: unknown; getCell(index: number): { numFmt: string } };
  getColumn(index: number): { width: number };
  views: unknown[];
  autoFilter: unknown;
  mergeCells(range: string): void;
}

interface Workbook {
  creator: string;
  created: Date;
  addWorksheet(name: string): Sheet;
  xlsx: { writeBuffer(): Promise<ArrayBuffer | Uint8Array> };
}

interface ExcelModule {
  Workbook: new () => Workbook;
}

let loader: Promise<ExcelModule> | null = null;

function excel(): Promise<ExcelModule> {
  loader ??= import('exceljs').then((module) => {
    const value = module as unknown as { default?: ExcelModule } & ExcelModule;
    return value.default ?? value;
  });
  return loader;
}

const FORMAT: Partial<Record<CellKind, string>> = {
  date: 'dd.mm.yyyy',
  money: '#,##0.00',
  number: '#,##0.###',
};

function cellValue(kind: CellKind, value: string | number | null): unknown {
  if (value === null || value === '') return null;
  if (kind === 'date' && typeof value === 'string') return new Date(`${value}T00:00:00.000Z`);
  return value;
}

export async function reportWorkbook(
  table: Pick<ReportTable, 'columns' | 'rows'>,
  heading: { title: string; period: string; generated: string },
): Promise<Uint8Array> {
  const { Workbook } = await excel();
  const workbook = new Workbook();
  workbook.creator = 'Felixmate';
  workbook.created = new Date();
  // Sayfa adı en çok 31 karakter; Excel'in yasak karakterleri ayıklanır
  const sheet = workbook.addWorksheet(heading.title.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31));
  const title = sheet.addRow([heading.title]);
  title.font = { bold: true, size: 14 };
  sheet.addRow([heading.period]);
  sheet.addRow([heading.generated]);
  const header = sheet.addRow(table.columns.map((column) => column.label));
  header.font = { bold: true };
  for (const values of table.rows) {
    const row = sheet.addRow(
      values.map((value, index) => cellValue(table.columns[index]?.kind ?? 'text', value)),
    );
    for (const [index, column] of table.columns.entries()) {
      const format = FORMAT[column.kind];
      if (format) row.getCell(index + 1).numFmt = format;
    }
  }
  for (const [index, column] of table.columns.entries()) {
    const longest = Math.max(
      column.label.length,
      ...table.rows.map((values) => String(values[index] ?? '').length),
    );
    sheet.getColumn(index + 1).width = Math.min(48, Math.max(10, longest + 2));
  }
  // Başlık satırı sabit; süzgeç başlıkta (4. satır)
  sheet.views = [{ state: 'frozen', ySplit: 4 }];
  sheet.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4 + table.rows.length, column: table.columns.length },
  };
  const buffer = await workbook.xlsx.writeBuffer();
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

/**
 * Birden çok sayfalı Excel (ADR 0117 Ek Y7 dönem dışa aktarımı): her sayfa başlık satırı ve
 * satırlar; sayı hücreleri sayıdır, başlık satırı sabit ve süzgeçli.
 */
export async function sheetsWorkbook(
  sheets: readonly {
    name: string;
    head: readonly string[];
    rows: readonly (readonly (string | number | null)[])[];
  }[],
): Promise<Uint8Array> {
  const { Workbook } = await excel();
  const workbook = new Workbook();
  workbook.creator = 'Felixmate';
  workbook.created = new Date();
  for (const spec of sheets) {
    const sheet = workbook.addWorksheet(spec.name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31));
    const header = sheet.addRow([...spec.head]);
    header.font = { bold: true };
    for (const values of spec.rows) sheet.addRow([...values]);
    for (const [index, label] of spec.head.entries()) {
      const longest = Math.max(
        label.length,
        ...spec.rows.map((values) => String(values[index] ?? '').length),
      );
      sheet.getColumn(index + 1).width = Math.min(64, Math.max(10, longest + 2));
    }
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    if (spec.rows.length > 0) {
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1 + spec.rows.length, column: spec.head.length },
      };
    }
  }
  const buffer = await workbook.xlsx.writeBuffer();
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Dosyayı kullanıcıya verir (M12c-R5): kaynak kaydetme yolu verirse o (gemide ana süreç kaydet
 * penceresi; oturum `<a download>` indirmesini iptal eder), yoksa tarayıcı indirmesi.
 */
export async function saveReportFile(
  source: Pick<ReportSource, 'download'>,
  fileName: string,
  bytes: Uint8Array,
  type: string,
): Promise<void> {
  if (source.download) {
    await source.download(fileName, bytes, type);
    return;
  }
  downloadFile(fileName, bytes, type);
}

/** Dosyayı tarayıcıda indirir (`<a download>`). Gemide oturum indirmeyi iptal eder; orada kullanılmaz. */
export function downloadFile(fileName: string, bytes: Uint8Array, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
